import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { CONTACT } from "../data";

const LINKS = [
  { label: "The Arsenal", href: "#arsenal" },
  { label: "The Proof", href: "#proof" },
  { label: "Meet Luke", href: "#meet-luke" },
  { label: "The Praise", href: "#praise" },
  { label: "Book a Slot", href: "#booking" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-black/75 backdrop-blur-2xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 md:px-10">
        <a href="#top" onClick={(e) => { e.preventDefault(); go("#top"); }} data-testid="nav-logo" className="flex items-center gap-3">
          <img src="/ftc-logo.png" alt="FTC Automotive" className="h-11 w-11 rounded-md object-cover md:h-12 md:w-12" />
          <span className="font-display text-sm tracking-[0.25em] chrome-text hidden sm:block">FTC AUTOMOTIVE</span>
        </a>

        <div className="hidden items-center gap-9 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              onClick={() => go(l.href)}
              className="font-accent text-[13px] uppercase tracking-[0.2em] text-neutral-300 transition-colors duration-300 hover:text-white"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <a
            href={CONTACT.phoneHref}
            data-testid="nav-call-button"
            className="pulse-amber hidden items-center gap-2 rounded-full bg-[#ffb000] px-5 py-2.5 font-accent text-[13px] font-600 uppercase tracking-[0.18em] text-black transition-colors duration-300 hover:bg-[#ff8c00] sm:flex"
          >
            <Phone className="h-4 w-4" strokeWidth={2.5} />
            Call {CONTACT.owner} Now
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            className="grid h-11 w-11 place-items-center rounded-full border border-white/15 text-white lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-white/10 bg-black/95 backdrop-blur-2xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-5">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`nav-mobile-${l.href.slice(1)}`}
                  onClick={() => go(l.href)}
                  className="py-3 text-left font-accent text-base uppercase tracking-[0.2em] text-neutral-200"
                >
                  {l.label}
                </button>
              ))}
              <a href={CONTACT.phoneHref} className="mt-3 flex items-center justify-center gap-2 rounded-full bg-[#ffb000] py-3 font-accent uppercase tracking-[0.18em] text-black">
                <Phone className="h-4 w-4" /> Call {CONTACT.owner} — {CONTACT.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
